import React, { useState } from "react";
import { Briefcase, MapPin, ArrowRight, CheckCircle, Zap, Users, Code } from "../components/Icons";
import { Reveal, Button, SectionHeading, PageGlow, Card, Badge } from "../components/ui";

const OPENINGS = [
  /*{
    id: 1,
    title: "Senior Backend Architect",
    subtitle: "Go / Java · Distributed Systems",
    team: "Core Platform",
    location: "Bengaluru / Remote",
    type: "Full-time",
    salary: "₹30L – ₹55L",
    color: "emerald",
    description: "You'll design and own the core services that power Dowble's owned products and client platforms. From high-throughput Go APIs to JVM-tuned Java microservices, you'll architect systems built to handle millions of concurrent users with sub-100ms p99 latency.",
    responsibilities: [
      "Design distributed microservices architectures in Go and Java",
      "Optimize high-concurrency database access patterns with PostgreSQL and Redis",
      "Own API design, schema versioning, and backward compatibility",
      "Conduct rigorous code reviews and enforce architectural standards",
      "Collaborate with AI tooling pipelines to accelerate boilerplate generation",
      "Lead load testing and capacity planning for new product launches",
    ],
    requirements: [
      "5+ years of backend engineering experience in Go or Java (or both)",
      "Deep understanding of distributed systems — consensus, CAP theorem, eventual consistency",
      "Experience designing and operating microservices at scale (Kubernetes, Docker)",
      "Strong grasp of database internals — query planning, indexing, replication",
      "Experience with multi-cloud deployments (AWS, GCP, or Azure)",
      "Exceptional written communication for async-first collaboration",
    ],
    nice_to_have: ["gRPC / Protocol Buffers", "Kafka or Pulsar", "Rust or C++", "Open source contributions"],
  },*/
  {
    "id": 2,
    "title": "Full-Stack Engineering Intern",
    "subtitle": "Java · React · Android · iOS",
    "team": "Core Platform & Mobility",
    "location": "Bengaluru / Remote",
    "type": "Internship (6 Months)",
    "salary": ".",
    "color": "emerald",
    "description": "Join us to build high-performance web and mobile ecosystems at hyper-speed. As a Full-Stack Intern, you will work directly alongside senior backend architects and mobile engineers, shipping production code across our Java enterprise backends, React web apps, and native mobile environments.",
    "responsibilities": [
      "Develop and optimize robust backend REST APIs and microservices using Java and Spring Boot",
      "Build clean, responsive, and performance-driven web user interfaces with React.js and Tailwind CSS",
      "Assist in developing and debugging features for native Android (Kotlin/Java) and iOS (Swift) mobile applications",
      "Write optimized SQL queries and integrate database logic using PostgreSQL",
      "Participate in agile sprint cycles, senior peer code reviews, and architectural scoping sessions",
      "Implement automated unit and integration tests across web, mobile, and API layers"
    ],
    "requirements": [
      "Strong foundational knowledge of Java and Object-Oriented Programming (OOP) principles",
      "Hands-on academic or personal project experience building web applications with React.js",
      "Basic familiarity with Android Studio or Xcode, and mobile application filesystems",
      "Solid understanding of relational databases (SQL), data structures, and algorithmic complexity",
      "Familiarity with version control workflows using Git",
      "Currently pursuing or recently graduated with a degree in Computer Science, Software Engineering, or a related technical discipline"
    ],
    "nice_to_have": [
      "Prior tech internship or open-source contributions",
      "Familiarity with Spring Boot or Hibernate frameworks",
      "Experience with Docker or cloud basics (AWS/GCP)",
      "Active profile on competitive coding platforms (LeetCode, CodeChef)"
    ]
   },
  /*{
    id: 3,
    title: "Full-Stack Web Engineer",
    subtitle: "Next.js · Node.js · PostgreSQL",
    team: "Web Platforms",
    location: "Bengaluru / Remote",
    type: "Full-time",
    salary: "₹18L – ₹35L",
    color: "violet",
    description: "You'll build the web platforms at the heart of Dowble's client SaaS products — from server-rendered Next.js frontends to Node.js API layers backed by PostgreSQL. You ship fast, care deeply about UX, and write code you're proud of.",
    responsibilities: [
      "Build responsive, accessible web applications with Next.js and React",
      "Design and implement RESTful and GraphQL APIs with Node.js",
      "Model complex relational schemas in PostgreSQL with performance in mind",
      "Implement authentication systems — OAuth 2.0, JWT, role-based access",
      "Integrate third-party services: payments, analytics, CRM, email",
      "Write end-to-end tests with Playwright and unit tests with Vitest",
    ],
    requirements: [
      "3+ years of full-stack JavaScript/TypeScript development",
      "Strong proficiency in Next.js including App Router, ISR, and Server Components",
      "Solid Node.js API development experience with Express or Fastify",
      "PostgreSQL proficiency — schema design, query optimization, migrations",
      "Experience with cloud deployment — Vercel, AWS, or GCP",
      "Eye for design and ability to translate Figma specs with pixel precision",
    ],
    nice_to_have: ["Prisma / Drizzle ORM", "Redis caching", "tRPC", "Edge computing (Cloudflare Workers)"],
  },*/
];

const PERKS = [
  { icon: Zap,         label: "Fast-paced shipping culture",        desc: "Ship real products to real users every 15 days. No endless planning cycles." },
  { icon: Code,        label: "Own products + client work",         desc: "Work on both Dowble's internally-owned apps and high-impact client platforms." },
  { icon: Users,       label: "Elite team, small & focused",        desc: "Work alongside engineers who've shipped products with millions of users." },
  { icon: CheckCircle, label: "Remote-first with async culture",    desc: "Work from anywhere in India. Async-first with clear documentation standards." },
];

const COLOR_MAP = {
  emerald: { border: "border-emerald-500/25", text: "text-emerald-600", dot: "bg-emerald-400" },
  indigo:  { border: "border-accent-500/25",  text: "text-accent-600",  dot: "bg-accent-400"  },
  violet:  { border: "border-violet-500/25",  text: "text-violet-600",  dot: "bg-violet-400"  },
};

function JobCard({ job }) {
  const [expanded, setExpanded] = useState(false);
  const c = COLOR_MAP[job.color] || COLOR_MAP.emerald;

  return (
    <article className={"card overflow-hidden transition-all duration-300 " + (expanded ? "border-zinc-300" : "")}>
      <div className="p-7">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-4 mb-5">
          <div>
            <div className={"inline-block px-2.5 py-1 rounded-lg text-xs font-semibold border bg-zinc-50 mb-3 " + c.border + " " + c.text}>
              {job.team}
            </div>
            <h2 className="text-zinc-900 font-bold text-xl mb-1 tracking-tight">{job.title}</h2>
            <p className={"text-sm font-medium " + c.text}>{job.subtitle}</p>
          </div>
          <div className="flex flex-col items-start sm:items-end gap-2 flex-shrink-0">
            <span className="text-zinc-900 font-bold text-sm">{job.salary}</span>
            <div className="flex items-center gap-3 text-zinc-500 text-xs">
              <span className="flex items-center gap-1">
                <MapPin size={12} />
                {job.location}
              </span>
              <span className="px-2 py-0.5 rounded-full bg-zinc-100 border border-zinc-200">{job.type}</span>
            </div>
          </div>
        </div>

        <p className="text-zinc-600 text-sm leading-relaxed mb-5">{job.description}</p>

        <button
          onClick={() => setExpanded(!expanded)}
          className={"text-sm font-semibold transition-colors " + c.text + " hover:text-zinc-900"}
          aria-expanded={expanded}
        >
          {expanded ? "↑ Show less" : "↓ View full requirements"}
        </button>
      </div>

      <div className="grid transition-[grid-template-rows] duration-300 ease-out" style={{ gridTemplateRows: expanded ? "1fr" : "0fr" }}>
        <div className="overflow-hidden">
          <div className="border-t border-zinc-200 p-7 bg-zinc-50">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              <div>
                <h3 className="text-zinc-900 font-bold text-sm mb-4">Responsibilities</h3>
                <ul className="space-y-2.5">
                  {job.responsibilities.map((r) => (
                    <li key={r} className="flex gap-2.5 text-zinc-600 text-sm">
                      <span className={"mt-1.5 w-1.5 h-1.5 rounded-full flex-shrink-0 " + c.dot} />
                      {r}
                    </li>
                  ))}
                </ul>
              </div>
              <div>
                <h3 className="text-zinc-900 font-bold text-sm mb-4">Requirements</h3>
                <ul className="space-y-2.5 mb-6">
                  {job.requirements.map((r) => (
                    <li key={r} className="flex gap-2.5 text-zinc-600 text-sm">
                      <CheckCircle size={14} className="text-emerald-600 flex-shrink-0 mt-0.5" />
                      {r}
                    </li>
                  ))}
                </ul>
                <h3 className="text-zinc-900 font-bold text-sm mb-3">Nice to Have</h3>
                <div className="flex flex-wrap gap-2">
                  {job.nice_to_have.map((n) => (
                    <span key={n} className={"px-2.5 py-1 rounded-lg text-xs border bg-zinc-50 " + c.border + " " + c.text}>
                      {n}
                    </span>
                  ))}
                </div>
              </div>
            </div>
            <div className="mt-7 pt-6 border-t border-zinc-200">
              <Button
                href={"mailto:contact@dowble.com?subject=Application: " + job.title}
                target="_blank"
                rel="noreferrer"
              >
                Apply for This Role
                <ArrowRight size={15} />
              </Button>
            </div>
          </div>
        </div>
      </div>
    </article>
  );
}

export default function CareersPage({ navigate }) {
  return (
    <div className="min-h-screen">
      {/* Hero */}
      <section className="relative py-20 px-6 text-center overflow-hidden">
        <PageGlow />
        <div className="relative max-w-4xl mx-auto">
          <Reveal>
            <SectionHeading
              eyebrow="Join the Core"
              title={<>Build Products That <span className="text-gradient-brand">Millions Actually Use.</span></>}
              subtitle="We're looking for elite engineers who want to ship real products at real speed. No bureaucracy, no scope creep, no half-finished projects. Just relentless execution."
            />
          </Reveal>
        </div>
      </section>

      {/* Perks */}
      <section className="py-16 px-6 border-t border-zinc-200" aria-label="Why Dowble">
        <div className="max-w-7xl mx-auto grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
          {PERKS.map((perk, i) => {
            const Icon = perk.icon;
            return (
              <Reveal key={perk.label} delay={i * 70}>
                <Card interactive className="p-6 h-full">
                  <span className="w-11 h-11 rounded-xl bg-gradient-to-br from-accent-500/30 to-violet-500/10 border border-zinc-200 flex items-center justify-center text-accent-600 mb-4">
                    <Icon size={20} />
                  </span>
                  <p className="text-zinc-900 font-bold text-sm mb-2">{perk.label}</p>
                  <p className="text-zinc-600 text-xs leading-relaxed">{perk.desc}</p>
                </Card>
              </Reveal>
            );
          })}
        </div>
      </section>

      {/* Job Listings */}
      <section className="py-16 px-6" aria-labelledby="openings-heading">
        <div className="max-w-5xl mx-auto">
          <Reveal className="mb-10">
            <div className="flex items-center gap-3 mb-2">
              <h2 id="openings-heading" className="text-2xl sm:text-3xl font-bold tracking-tight text-zinc-900">Open Roles</h2>
              <Badge className="text-accent-700 border-accent-500/25 bg-accent-500/10">
                {OPENINGS.length} {OPENINGS.length === 1 ? "position" : "positions"}
              </Badge>
            </div>
            <p className="text-zinc-600 text-sm">Click any role to expand full requirements and apply.</p>
          </Reveal>
          <div className="space-y-5">
            {OPENINGS.map((job, i) => (
              <Reveal key={job.id} delay={i * 70}>
                <JobCard job={job} />
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Open Application */}
      <section className="border-t border-zinc-200 py-20 px-6 text-center">
        <Reveal className="max-w-2xl mx-auto">
          <span className="inline-flex w-14 h-14 rounded-2xl bg-accent-500/12 border border-accent-500/25 items-center justify-center text-accent-600 mb-6">
            <Briefcase size={26} />
          </span>
          <h2 className="text-2xl sm:text-3xl font-bold tracking-tight text-zinc-900 mb-3">Don't see your role?</h2>
          <p className="text-zinc-600 text-sm mb-7 max-w-lg mx-auto">
            If you're an exceptional engineer who believes you can add something to what we're building,
            we want to hear from you. Send your portfolio and a note to{" "}
            <a href="mailto:contact@dowble.com" className="text-accent-600 hover:text-accent-700 hover:underline">contact@dowble.com</a>.
          </p>
          <Button size="lg" variant="secondary" href="mailto:contact@dowble.com?subject=Open Application">
            Send Open Application
            <ArrowRight size={16} />
          </Button>
        </Reveal>
      </section>
    </div>
  );
}
